import { resolveClient } from './supabase';
import type { Tenant } from '@lingaai/types';
import type { SupabaseClient } from '@supabase/supabase-js';

export async function getTenants(client?: SupabaseClient): Promise<Tenant[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('tenants').select('*').order('created_at', { ascending: false });

  if (error) throw error;
  return data as Tenant[];
}

export async function getTenant(tenantId: string, client?: SupabaseClient): Promise<Tenant> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('tenants').select('*').eq('id', tenantId).single();

  if (error) throw error;
  return data as Tenant;
}

export async function getTenantByWorkspaceSlug(workspaceSlug: string, client?: SupabaseClient): Promise<Tenant | null> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('tenants').select('*').eq('workspace_slug', workspaceSlug).single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    throw error;
  }
  return data as Tenant;
}

export async function createTenant(
  data: {
    name: string;
    workspace_slug: string;
    workspace_email: string;
  },
  client?: SupabaseClient
): Promise<Tenant> {
  const supabase = resolveClient(client);

  const { data: tenant, error } = await supabase.from('tenants').insert(data).select().single();

  if (error) throw error;
  return tenant as Tenant;
}

export async function updateTenant(tenantId: string, updates: Partial<Tenant>, client?: SupabaseClient): Promise<Tenant> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('tenants').update(updates).eq('id', tenantId).select().single();

  if (error) throw error;
  return data as Tenant;
}

/**
 * Update retention policy for a tenant.
 * Convenience function for updateTenant.
 */
export async function updateRetentionPolicy(tenantId: string, policy: 'delete_immediately' | 'keep_30_days' | 'keep_90_days' | 'keep_manual', client?: SupabaseClient): Promise<Tenant> {
  return updateTenant(tenantId, { document_retention_policy: policy }, client);
}
